import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'

import { useAppContext } from './AppContext.jsx'
import { PageHeader, PageFooter, SetAnchor } from './PageCommon.jsx'

import es from '../locales/mainPage_es.json'
import en from '../locales/mainPage_en.json'
import './PageCommon.css'

function HomePage({ section }) {
    const { lang, getLanguageObject } = useAppContext();
    const t = getLanguageObject(lang, es, en);

    useEffect(() => {
        if (!section) return;
        const el = document.getElementById(section);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [section]);

    function toggleFaq(e) {
        const btn = e.currentTarget;
        const item = btn.parentElement;
        const isOpen = item.classList.contains('open');
        // close all
        document.querySelectorAll('.faq-item').forEach(el => el.classList.remove('open'));
        if (!isOpen) item.classList.add('open');
    }

    return (
        <>
            {/* <!-- ── NAV ──────────────────────────────────────────── --> */}
            <PageHeader />

            {/* <!-- ── HERO ─────────────────────────────────────────── --> */}
            <section>
                <div className="hero-wrap">
                    <div className="hero-text">
                        <div className="hero-badge"><span>🤖</span> {t.hero.badge}</div>
                        <h1 className="hero-title">
                            {t.hero.titlePre}<span className="accent">{t.hero.titleAccent}</span>
                        </h1>
                        <p className="hero-sub">
                            {t.hero.subtitle}
                        </p>
                        <div className="hero-ctas">
                            <Link to="/pricing" className="btn btn-primary">{t.hero.ctaStart}</Link>
                            <Link to="/how-it-works" className="btn btn-secondary">{t.hero.ctaHow}</Link>
                        </div>
                        <p className="hero-fine" dangerouslySetInnerHTML={{ __html: t.hero.finePrint }}></p>
                    </div>

                    <div className="hero-visual">
                        <div className="hero-blob"></div>

                        {/* <!-- floating badge left --> */}
                        <div className="float-badge float-badge-1">
                            <div className="fb-icon">🥑</div>
                            <div>
                                <div className="fb-label">{t.hero.detectedPlate}</div>
                                <div className="fb-val">{t.hero.detectedPlateVal}</div>
                            </div>
                        </div>

                        {/* <!-- phone --> */}
                        <div className="phone-main">
                            <div className="phone-notch"></div>
                            <div className="phone-screen">
                                <div className="phone-header-bar">
                                    <div className="phone-greeting">{t.hero.greeting}</div>
                                    <div className="phone-date">{t.hero.meta}</div>
                                </div>

                                {/* <!-- calorie ring --> */}
                                <div className="phone-deficit-ring">
                                    <div className="ring-track">
                                        <div className="ring-inner">
                                            <div className="ring-kcal">{t.hero.kcalRemainingVal}</div>
                                            <div className="ring-label">{t.hero.kcalRemainingLbl}</div>
                                        </div>
                                    </div>
                                    <div className="deficit-row">
                                        <div className="deficit-macro"><div className="val">82g</div><div className="lbl">{t.hero.protein}</div></div>
                                        <div className="deficit-macro"><div className="val">45g</div><div className="lbl">{t.hero.fat}</div></div>
                                        <div className="deficit-macro"><div className="val">120g</div><div className="lbl">{t.hero.carbs}</div></div>
                                    </div>
                                </div>

                                {/* <!-- meal log --> */}
                                <div className="phone-meal-log">
                                    <div className="meal-label">{t.hero.todayMeals}</div>
                                    <div className="meal-row"><div className="meal-icon">🥚</div><div className="meal-name">{t.hero.breakfast}</div><div className="meal-kcal">320 kcal</div></div>
                                    <div className="meal-row"><div className="meal-icon">🍗</div><div className="meal-name">{t.hero.lunch}</div><div className="meal-kcal">280 kcal</div></div>
                                    <div className="meal-row"><div className="meal-icon">🍎</div><div className="meal-name">{t.hero.snack}</div><div className="meal-kcal">80 kcal</div></div>
                                </div>
                            </div>
                        </div>

                        {/* <!-- floating badge right --> */}
                        <div className="float-badge float-badge-2">
                            <div className="fb-icon">🎯</div>
                            <div>
                                <div className="fb-label">{t.hero.deficit}</div>
                                <div className="fb-val">{t.hero.deficitVal}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* <!-- ── STATS BAR ─────────────────────────────────────── --> */}
            <div className="stats-bar">
                <div className="stats-inner">
                    <div className="stat-item">
                        <div className="stat-number">10K+</div>
                        <div className="stat-label">{t.stats.activeUsers}</div>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-item">
                        <div className="stat-number">4.9</div>
                        <div className="stat-stars">★★★★★</div>
                        <div className="stat-label">{t.stats.rating}</div>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-item">
                        <div className="stat-number">92%</div>
                        <div className="stat-label">{t.stats.accuracy}</div>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-item">
                        <div className="stat-number">&lt;30s</div>
                        <div className="stat-label">{t.stats.speed}</div>
                    </div>
                </div>
            </div>

            {/* <!-- ── CÓMO FUNCIONA ─────────────────────────────────── --> */}
            <SetAnchor id="how-it-works"></SetAnchor>
            <section className="section" id="como-funciona">
                <div className="container text-center">
                    <span className="section-label">{t.howItWorks.label}</span>
                    <h2 className="section-title">
                        {t.howItWorks.titlePart1}<br />{t.howItWorks.titlePart2}
                    </h2>
                    <p className="section-sub">{t.howItWorks.subtitle}</p>

                    <div className="register-grid">
                        {/* <!-- foto --> */}
                        <div className="register-card">
                            <h3>{t.howItWorks.card1.title}</h3>
                            <div className="register-phone">
                                <div className="register-phone-notch"></div>
                                <div className="register-phone-screen">
                                    <div className="rp-icon">📸</div>
                                    <div className="rp-scanner">
                                        <div className="rp-scan-line"></div>
                                        <span className="rp-scan-emoji">🥗</span>
                                    </div>
                                    <div className="rp-text">{t.howItWorks.card1.scanner}</div>
                                    <div className="rp-bar rp-bar-active rp-bar-w-60"></div>
                                </div>
                            </div>
                            <p>{t.howItWorks.card1.desc}</p>
                        </div>

                        {/* <!-- voz --> */}
                        <div className="register-card">
                            <h3>{t.howItWorks.card2.title}</h3>
                            <div className="register-phone">
                                <div className="register-phone-notch"></div>
                                <div className="register-phone-screen">
                                    <div className="rp-icon">🎙️</div>
                                    <div className="rp-voice-icon-bg">🔊</div>
                                    <div className="rp-text">{t.howItWorks.card2.voice}</div>
                                </div>
                            </div>
                            <p>{t.howItWorks.card2.desc}</p>
                        </div>

                        {/* <!-- texto --> */}
                        <div className="register-card">
                            <h3>{t.howItWorks.card3.title}</h3>
                            <div className="register-phone">
                                <div className="register-phone-notch"></div>
                                <div className="register-phone-screen">
                                    <div className="rp-icon">✏️</div>
                                    <div className="rp-bar rp-bar-active rp-bar-w-80"></div>
                                    <div className="rp-bar rp-bar-w-60-mt"></div>
                                    <div className="rp-text">{t.howItWorks.card3.text}</div>
                                </div>
                            </div>
                            <p>{t.howItWorks.card3.desc}</p>
                        </div>

                        {/* <!-- recetas propias --> */}
                        <div className="register-card">
                            <h3>{t.howItWorks.card4.title}</h3>
                            <div className="register-phone">
                                <div className="register-phone-notch"></div>
                                <div className="register-phone-screen">
                                    <div className="rp-icon">🍲</div>
                                    <div className="rp-text rp-text-recipe">{t.howItWorks.card4.recipe}</div>
                                    <div className="rp-bar rp-bar-active rp-bar-w-90"></div>
                                    <div className="rp-bar rp-bar-active rp-bar-w-90"></div>
                                </div>
                            </div>
                            <p>{t.howItWorks.card4.desc}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* <!-- ── FEATURES SHOWCASE ─────────────────────────────── --> */}
            <SetAnchor id="features"></SetAnchor>
            <section className="section section-cream">
                <div className="container">
                    <div className="text-center m-bottom-64">
                        <span className="section-label">{t.features.label}</span>
                        <h2 className="section-title">
                            {t.features.titlePart1}<br />{t.features.titlePart2}
                        </h2>
                        <p className="section-sub">{t.features.subtitle}</p>
                    </div>

                    {/* <!-- Feature 1: Dashboard --> */}
                    <div className="feature-row">
                        <div className="feature-content">
                            <div className="feature-icon-big">📊</div>
                            <h3 className="feature-title">{t.features.f1.title}</h3>
                            <p className="feature-desc">{t.features.f1.desc}</p>
                            <ul className="feature-list">
                                {t.features.f1.bullets.map((bullet, idx) => (
                                    <li key={idx}>{bullet}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="feature-visual mint">
                            <div className="feat-phone">
                                <div className="feat-phone-notch"></div>
                                <div className="feat-phone-screen">
                                    <div className="feat-card">
                                        <div className="feat-card-title">{t.features.f1.visual.title}</div>
                                        <div className="feat-card-row">
                                            <div className="feat-card-val">460</div>
                                            <div className="feat-card-label">{t.features.f1.visual.ofKcal}</div>
                                        </div>
                                        <div className="feat-progress"><div className="feat-progress-fill feat-progress-w-28"></div></div>
                                    </div>
                                    <div className="feat-card">
                                        <div className="feat-card-title">{t.features.f1.visual.protein}</div>
                                        <div className="feat-card-row"><div className="feat-card-val feat-card-val-gold">38g</div><div className="feat-card-label">{t.features.f1.visual.proteinOf}</div></div>
                                        <div className="feat-progress"><div className="feat-progress-fill feat-progress-w-32-green"></div></div>
                                    </div>
                                    <div className="feat-card">
                                        <div className="feat-card-title">{t.features.f1.visual.fat}</div>
                                        <div className="feat-card-row"><div className="feat-card-val feat-card-val-orange">15g</div><div className="feat-card-label">{t.features.f1.visual.fatOf}</div></div>
                                        <div className="feat-progress"><div className="feat-progress-fill feat-progress-w-28-orange"></div></div>
                                    </div>
                                    <div className="feat-progress-caption">{t.features.f1.visual.caption}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* <!-- Feature 2: FynBot --> */}
                    <div className="feature-row reverse">
                        <div className="feature-content">
                            <div className="feature-icon-big">🤖</div>
                            <h3 className="feature-title">{t.features.f2.title}</h3>
                            <p className="feature-desc">{t.features.f2.desc}</p>
                            <ul className="feature-list">
                                {t.features.f2.bullets.map((bullet, idx) => (
                                    <li key={idx}>{bullet}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="feature-visual yellow">
                            <div className="feat-phone">
                                <div className="feat-phone-notch"></div>
                                <div className="feat-phone-screen">
                                    <div className="feat-bot-title">{t.features.f2.visual.title}</div>
                                    <div className="feat-bot-bubble">{t.features.f2.visual.bubble1}</div>
                                    <div className="feat-user-bubble">{t.features.f2.visual.user1}</div>
                                    <div className="feat-bot-bubble" dangerouslySetInnerHTML={{ __html: t.features.f2.visual.bubble2 }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* <!-- Feature 3: Meal Plan --> */}
                    <div className="feature-row">
                        <div className="feature-content">
                            <div className="feature-icon-big">📅</div>
                            <h3 className="feature-title">{t.features.f3.title}</h3>
                            <p className="feature-desc">{t.features.f3.desc}</p>
                            <ul className="feature-list">
                                {t.features.f3.bullets.map((bullet, idx) => (
                                    <li key={idx}>{bullet}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="feature-visual lavender">
                            <div className="feat-phone">
                                <div className="feat-phone-notch"></div>
                                <div className="feat-phone-screen">
                                    <div className="feat-plan-title">{t.features.f3.visual.title}</div>
                                    <div className="feat-plan-row"><div className="feat-plan-icon">🌅</div><div className="feat-plan-text">{t.features.f3.visual.breakfast}</div><div className="feat-plan-kcal">320 kcal</div></div>
                                    <div className="feat-plan-row"><div className="feat-plan-icon">☀️</div><div className="feat-plan-text">{t.features.f3.visual.lunch}</div><div className="feat-plan-kcal">480 kcal</div></div>
                                    <div className="feat-plan-row"><div className="feat-plan-icon">🍎</div><div className="feat-plan-text">{t.features.f3.visual.snack}</div><div className="feat-plan-kcal">180 kcal</div></div>
                                    <div className="feat-plan-row"><div className="feat-plan-icon">🌙</div><div className="feat-plan-text">{t.features.f3.visual.dinner}</div><div className="feat-plan-kcal">280 kcal</div></div>
                                    <div className="feat-plan-summary">
                                        <div className="feat-plan-summary-lbl">{t.features.f3.visual.total}</div>
                                        <div className="feat-plan-summary-val">1,260 kcal</div>
                                        <div className="feat-plan-summary-status">{t.features.f3.visual.status}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            {/* <!-- ── BENEFITS GRID ─────────────────────────────────── --> */}
            <SetAnchor id="benefits"></SetAnchor>
            <section className="section">
                <div className="container text-center">
                    <span className="section-label">{t.benefits.label}</span>
                    <h2 className="section-title">{t.benefits.title}</h2>
                    <p className="section-sub">{t.benefits.subtitle}</p>

                    <div className="benefits-grid">
                        {t.benefits.cards.map((card, idx) => {
                            const bgClasses = ['bi-yellow', 'bi-green', 'bi-sage', 'bi-lavender', 'bi-peach', 'bi-cream'];
                            const bgClass = bgClasses[idx % bgClasses.length];
                            return (
                                <div className="benefit-card" key={idx}>
                                    <div className={`benefit-icon ${bgClass}`}>{card.icon}</div>
                                    <h3>{card.title}</h3>
                                    <p>{card.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* <!-- ── TESTIMONIALS ──────────────────────────────────── --> */}
            <SetAnchor id="testimonials"></SetAnchor>
            <section className="section section-cream">
                <div className="container text-center">
                    <span className="section-label">{t.testimonials.label}</span>
                    <h2 className="section-title">{t.testimonials.title}</h2>
                    <p className="section-sub">{t.testimonials.subtitle}</p>

                    <div className="testimonials-grid">
                        {t.testimonials.cards.map((card, idx) => {
                            const avatarClasses = ['av-g', 'av-gold', 'av-sage'];
                            const avatarClass = avatarClasses[idx % avatarClasses.length];
                            const initial = card.name.charAt(0);
                            return (
                                <div className="testimonial-card" key={idx}>
                                    <div className="testi-stars">★★★★★</div>
                                    <p className="testi-quote">{card.quote}</p>
                                    <div className="testi-user">
                                        <div className={`testi-avatar ${avatarClass}`}>{initial}</div>
                                        <div>
                                            <div className="testi-name">{card.name}</div>
                                            <div className="testi-tag">{card.tag}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* <!-- ── PRICING ────────────────────────────────────────── --> */}
            <SetAnchor id="pricing"></SetAnchor>
            <section className="section pricing-section">
                <div className="container text-center">
                    <span className="section-label">{t.pricing.label}</span>
                    <h2 className="section-title">
                        {t.pricing.titlePart1}<br />{t.pricing.titlePart2}
                    </h2>
                    <p className="section-sub">{t.pricing.subtitle}</p>

                    <div className="pricing-grid">
                        {/* <!-- Free --> */}
                        <div className="price-card">
                            <div className="price-name">{t.pricing.free.name}</div>
                            <div className="price-cost">{t.pricing.free.cost} <span>{t.pricing.free.perMonth}</span></div>
                            <div className="price-period">{t.pricing.free.period}</div>
                            <ul className="price-features">
                                {t.pricing.free.features.map((feat, idx) => (
                                    <li key={idx}>
                                        {feat.included ? (
                                            <>
                                                <span className="check">✓</span> {feat.text}
                                            </>
                                        ) : (
                                            <>
                                                <span className="cross">✗</span> {feat.text}
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <button className="btn-plan btn-plan-ghost">{t.pricing.free.button}</button>
                        </div>

                        {/* <!-- Basic --> */}
                        <div className="price-card featured">
                            <div className="price-badge">{t.pricing.basic.badge}</div>
                            <div className="price-name">{t.pricing.basic.name}</div>
                            <div className="price-cost">{t.pricing.basic.cost} <span>{t.pricing.basic.perMonth}</span></div>
                            <div className="price-period">{t.pricing.basic.period}</div>
                            <ul className="price-features">
                                {t.pricing.basic.features.map((feat, idx) => (
                                    <li key={idx}>
                                        {feat.included ? (
                                            <>
                                                <span className="check">✓</span> {feat.text}
                                            </>
                                        ) : (
                                            <>
                                                <span className="cross">✗</span> {feat.text}
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <button className="btn-plan btn-plan-primary">{t.pricing.basic.button}</button>
                        </div>

                        {/* <!-- Premium --> */}
                        <div className="price-card">
                            <div className="price-name">{t.pricing.premium.name}</div>
                            <div className="price-cost">{t.pricing.premium.cost} <span>{t.pricing.premium.perMonth}</span></div>
                            <div className="price-period">{t.pricing.premium.period}</div>
                            <ul className="price-features">
                                {t.pricing.premium.features.map((feat, idx) => (
                                    <li key={idx}>
                                        {feat.included ? (
                                            <>
                                                <span className="check">✓</span> {feat.text}
                                            </>
                                        ) : (
                                            <>
                                                <span className="cross">✗</span> {feat.text}
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <button className="btn-plan btn-plan-ghost">{t.pricing.premium.button}</button>
                        </div>
                    </div>
                </div>
            </section>

            {/* <!-- ── FAQ ───────────────────────────────────────────── --> */}
            <SetAnchor id="faq"></SetAnchor>
            <section className="section">
                <div className="container text-center">
                    <span className="section-label">{t.faq.label}</span>
                    <h2 className="section-title">{t.faq.title}</h2>

                    <div className="faq-list">
                        {t.faq.items.map((item, idx) => (
                            <div className="faq-item" key={idx}>
                                <button className="faq-q" onClick={toggleFaq}>
                                    <span>{item.q}</span>
                                    <div className="faq-icon">+</div>
                                </button>
                                <div className="faq-a">{item.a}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* <!-- ── FINAL CTA ──────────────────────────────────────── --> */}
            <SetAnchor id="final-cta"></SetAnchor>
            <section className="final-cta-section">
                <div className="container">
                    <h2 className="section-title">
                        {t.finalCta.titlePart1}<br />{t.finalCta.titlePart2}
                    </h2>
                    <p className="section-sub section-sub-mt">{t.finalCta.subtitle}</p>
                    <div className="cta-group">
                        <Link to="/pricing" className="btn btn-primary btn-hero-cta">{t.finalCta.ctaStart}</Link>
                        <Link to="/how-it-works" className="btn btn-outline-white">{t.finalCta.ctaHow}</Link>
                    </div>
                    <p className="final-cta-fine">{t.finalCta.finePrint}</p>
                </div>
            </section>

            {/* <!-- ── FOOTER ─────────────────────────────────────────── --> */}
            <PageFooter />
        </>
    )
}

export default HomePage
